import { Link } from "react-router-dom";
import PostCard from "@/components/feature/PostCard";
import {
  categories,
  trendingList,
  avoidHighlights,
  orderGuides,
  posts,
} from "@/mocks/anime";

function SectionHead({
  eyebrow,
  title,
  desc,
  to,
  toLabel,
  tone = "primary",
}: {
  eyebrow: string;
  title: string;
  desc: string;
  to?: string;
  toLabel?: string;
  tone?: "primary" | "accent" | "secondary";
}) {
  const eyebrowColor =
    tone === "accent" ? "text-accent-700" : tone === "secondary" ? "text-secondary-700" : "text-primary-700";
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-2xl">
        <span className={`text-xs font-bold uppercase tracking-widest ${eyebrowColor}`}>{eyebrow}</span>
        <h2 className="mt-2 font-heading text-2xl text-foreground-900 md:text-3xl">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-foreground-600">{desc}</p>
      </div>
      {to && toLabel && (
        <Link
          to={to}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-sm font-bold text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:text-primary-700"
        >
          {toLabel}
          <i className="ri-arrow-right-line"></i>
        </Link>
      )}
    </div>
  );
}

export function CategoriesSection() {
  return (
    <section className="container-x py-14 md:py-16">
      <SectionHead
        eyebrow="栏目导航"
        title="四大栏目，各有所爱"
        desc="求番、避雷、补番、漫评，四个入口覆盖同好的全部需求，点进去就能找到你要的。"
      />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {categories.map((c) => (
          <Link
            key={c.id}
            to={c.slug}
            className="card group relative overflow-hidden transition-colors duration-200 hover:border-primary-300"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={c.image}
                alt={c.name}
                title={c.title}
                className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 to-transparent"></div>
              <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-xl bg-background-50/90 text-lg text-primary-700">
                <i className={c.icon}></i>
              </span>
            </div>
            <div className="p-4">
              <h3 className="font-heading text-lg text-foreground-900">{c.name}</h3>
              <p className="mt-1.5 text-xs leading-5 text-foreground-600 md:text-sm">{c.tagline}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary-700">
                去看看
                <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1"></i>
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function TrendingSection() {
  return (
    <section className="bg-background-100">
      <div className="container-x py-14 md:py-16">
        <SectionHead
          eyebrow="今日热门"
          title="同好都在追的热门内容"
          desc="按人气与口碑精选的热门推荐与深度内容，错过就亏的宝藏都在这里。"
          to="/review"
          toLabel="查看更多"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {trendingList.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function AvoidSection() {
  return (
    <section className="container-x py-14 md:py-16">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
        <div>
          <SectionHead
            eyebrow="避雷精选"
            title="烂番不踩坑，时间不白花"
            desc="避雷指南把老番新番的雷点一次说清：烂尾、崩坏、节奏拖沓、设定崩盘，让你把有限的追番时间花在真正值得的番上。"
            to="/avoid"
            toLabel="进避雷指南"
          />
          <div className="flex flex-col gap-4">
            {avoidHighlights.slice(0, 2).map((p) => (
              <PostCard key={p.id} post={p} horizontal />
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <div className="card flex items-start gap-4 border-secondary-200 bg-secondary-50 p-5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary-500 text-xl text-background-50">
              <i className="ri-shield-cross-line"></i>
            </span>
            <div>
              <h3 className="font-bold text-foreground-900">避雷底线三问</h3>
              <p className="mt-1.5 text-sm leading-6 text-foreground-600">
                这部番口碑是否两极分化？节奏是否存在硬伤？制作有没有明显崩坏？三问答完，值不值得补心里就有数了。
              </p>
            </div>
          </div>
          {avoidHighlights.slice(2).map((p) => (
            <PostCard key={p.id} post={p} horizontal />
          ))}
        </div>
      </div>
    </section>
  );
}

export function OrderSection() {
  return (
    <section className="bg-gradient-to-br from-primary-950 to-primary-800">
      <div className="container-x py-14 md:py-16">
        <div className="mb-8 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest text-accent-300">补番攻略</span>
          <h2 className="mt-2 font-heading text-2xl text-background-50 md:text-3xl">按顺序补完老番，一站搞定</h2>
          <p className="mt-2 text-sm leading-6 text-background-50/80">
            连载顺序、剧情顺序、时间线顺序一次理清，追老番不再混乱。跟着攻略走，一路补到大结局。
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          {orderGuides.map((p) => (
            <PostCard key={p.id} post={p} />
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 rounded-2xl bg-background-50/10 p-6 backdrop-blur sm:grid-cols-4">
          {[
            { step: "01", title: "选对入口", desc: "先确定你要补的是哪一类老番" },
            { step: "02", title: "理清顺序", desc: "按连载 / 剧情时间线排好顺序" },
            { step: "03", title: "避雷筛选", desc: "先看避雷，避免踩中烂尾" },
            { step: "04", title: "放心开补", desc: "跟着攻略一路补到大结局" },
          ].map((s) => (
            <div key={s.step} className="flex gap-3">
              <span className="font-latin text-2xl font-extrabold text-accent-400">{s.step}</span>
              <div>
                <h3 className="font-bold text-background-50">{s.title}</h3>
                <p className="mt-1 text-xs leading-5 text-background-50/75">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ReviewSection() {
  const reviews = posts.filter((p) => p.category === "review").slice(0, 3);
  return (
    <section className="container-x py-14 md:py-16">
      <SectionHead
        eyebrow="漫评讨论"
        title="与同好聊到天亮"
        desc="私藏安利、深度漫评、剧情脑洞与角色分析，在这里分享你对每一部番的热爱。"
        to="/review"
        toLabel="进漫评讨论"
        tone="secondary"
      />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {reviews.map((p) => (
          <PostCard key={p.id} post={p} />
        ))}
      </div>
    </section>
  );
}